import type { BoxProps, Theme } from "@mui/material";
import { Box, useTheme } from "@mui/material";
import { useLayoutEffect, useRef } from "react";
import { UPLOADED_IMAGE_FILE_ID_ATTRIBUTE } from "../../utilities/htmlHelper";

export const ARTICLE_FONT_SCALE = 1.2;

interface Props {
  htmlContent: string;
  containerProps?: BoxProps;
  extraStyles?: string;
  fontScale?: number;
}

const createStylesFromTheme = (theme: Theme, fontScale: number) => {
  const {
    typography: { body1 },
    palette: {
      primary: { main },
    },
  } = theme;

  return `
      :host {
        display: block;
        font-size: ${fontScale}em;
      }

      p {
        font-family: ${body1.fontFamily};
        font-size: calc(${body1.fontSize} * ${fontScale});
        font-weight: ${body1.fontWeight};
        line-height: ${body1.lineHeight};
        letter-spacing: ${body1.letterSpacing};
        margin: 0;
        overflow-wrap: break-word;
      }

      /* The editor saves blank lines as empty paragraphs, which otherwise collapse to zero height. */
      p:empty::before {
        content: "\\00a0";
      }

      a {
        font-family: ${body1.fontFamily};
        font-size: calc(${body1.fontSize} * ${fontScale});
        font-weight: ${body1.fontWeight};
        line-height: ${body1.lineHeight};
        letter-spacing: ${body1.letterSpacing};
        margin: 0;
        text-decoration: none;
        color: ${main};
      }

      a:hover {
        text-decoration: underline;
      }

      ul {
        margin-block-start: 0.5em;
        margin-block-end: 0.5em;
      }

      ol ol:not([type]) {
        list-style-type: lower-alpha;
      }

      ol ol ol:not([type]) {
        list-style-type: lower-roman;
      }

      pre {
        margin: 8px 0;
        background: ${theme.palette.action.hover};
        padding: 8px 12px;
        border-radius: 4px;
        overflow: auto;
        white-space: pre-wrap;
        word-break: break-word;
      }

      code {
        background: ${theme.palette.action.selected};
        padding: 0 4px;
        border-radius: 3px;
        font-size: 0.88em;
      }

      pre code {
        background: transparent;
        padding: 0;
        font-size: inherit;
      }

      blockquote {
        border-left: 3px solid ${theme.palette.divider};
        margin: 1em 0;
        padding-left: 12px;
        color: ${theme.palette.text.secondary};
      }

      img[${UPLOADED_IMAGE_FILE_ID_ATTRIBUTE}] {
        margin-top: 1em;
        margin-bottom: 1em;
      }
    `;
};

const HtmlPreview = ({ htmlContent, containerProps, extraStyles, fontScale }: Props) => {
  const theme = useTheme();
  const shadowHostRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const shadowHost = shadowHostRef.current;
    if (!shadowHost) {
      return;
    }

    const shadowRoot = shadowHost.shadowRoot ?? shadowHost.attachShadow({ mode: "open" });
    shadowRoot.innerHTML = "";

    const styleElement = document.createElement("style");
    styleElement.textContent = createStylesFromTheme(theme, fontScale ?? 1) + (extraStyles ?? "");

    const contentDiv = document.createElement("div");
    contentDiv.innerHTML = htmlContent;

    shadowRoot.appendChild(styleElement);
    shadowRoot.appendChild(contentDiv);

    return () => {
      if (shadowRoot) {
        shadowRoot.innerHTML = "";
      }
    };
  }, [htmlContent, theme, extraStyles, fontScale]);

  return (
    <Box {...containerProps}>
      <div ref={shadowHostRef}></div>
    </Box>
  );
};

export default HtmlPreview;
